import React, { useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { countryDetails } from "../data/countryData";
import { companyInfo } from "../data/mockData";
import {
  ArrowRight,
  MapPin,
  GraduationCap,
  Globe,
  DollarSign,
  Clock,
  FileCheck,
  BookOpen,
  Users,
  CheckCircle,
  AlertCircle,
  Lightbulb,
  ChevronDown,
  ChevronUp,
  Star,
  Briefcase,
  Heart,
  Monitor,
  Wrench,
  Brain,
  TrendingUp,
  Scale,
  Landmark,
  Leaf,
  Utensils,
  Paintbrush,
  Car,
  Plane,
  BadgeCheck,
  Info,
} from "lucide-react";

const iconMap = {
  Monitor, Briefcase, Wrench, Heart, Brain, TrendingUp, Scale,
  Landmark, Leaf, Utensils, BookOpen, Paintbrush, Car,
};

const FadeIn = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const DemandBadge = ({ demand }) => {
  const colors = {
    "Very High": "bg-green-100 text-green-700",
    High: "bg-blue-100 text-blue-700",
    Medium: "bg-yellow-100 text-yellow-700",
  };
  return (
    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${colors[demand] || "bg-slate-100 text-slate-600"}`}>
      {demand}
    </span>
  );
};

const CountryDetailPage = () => {
  const { slug } = useParams();
  const country = countryDetails[slug];
  const [expandedUni, setExpandedUni] = useState(null);
  const [showAllUnis, setShowAllUnis] = useState(false);

  if (!country) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Country Not Found</h2>
          <p className="text-slate-500 mb-4">The country you're looking for doesn't exist.</p>
          <Link to="/" className="text-orange-500 hover:underline">Go back home</Link>
        </div>
      </div>
    );
  }

  const displayedUnis = showAllUnis ? country.universities : country.universities.slice(0, 5);

  return (
    <div>
      {/* Hero Banner */}
      <section className="relative h-[50vh] min-h-[380px] overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${country.heroImage})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/75 to-slate-900/50" />
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 w-full">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="flex items-center gap-3 mb-4">
                <img src={country.flag} alt={country.code} className="w-12 h-8 rounded shadow object-cover" />
                <span className="text-orange-400 text-sm font-medium tracking-wider uppercase">Study in {country.name}</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3">
                Study in <span className="text-orange-400">{country.name}</span>
              </h1>
              <p className="text-slate-300 text-lg max-w-2xl mb-6">{country.tagline}</p>
              <div className="flex flex-wrap gap-3">
                <Link to="/contact" className="group px-6 py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-all hover:shadow-lg hover:shadow-orange-500/30 flex items-center gap-2">
                  Get Free Counselling <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <a href="#universities" className="px-6 py-3 border border-white/30 text-white rounded-lg font-medium hover:bg-white/10 transition-all">
                  View Universities
                </a>
              </div>
            </motion.div>
          </div>
        </div>
        <motion.div animate={{ y: [0, -12, 0] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }} className="absolute top-24 right-20 z-10 hidden lg:block">
          <div className="w-14 h-14 bg-orange-500/20 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-orange-500/30">
            <Plane size={24} className="text-orange-400" />
          </div>
        </motion.div>
      </section>

      {/* Quick Facts Bar */}
      <section className="relative -mt-14 z-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: DollarSign, label: "Avg. Tuition", value: country.quickFacts.avgTuition },
              { icon: Globe, label: "Language", value: country.quickFacts.language },
              { icon: Clock, label: "Work Permit", value: country.quickFacts.workPermit },
              { icon: GraduationCap, label: "Intake Seasons", value: country.quickFacts.intakeSeasons },
            ].map((fact, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <fact.icon size={18} className="text-orange-500" />
                </div>
                <div>
                  <p className="text-xs text-slate-500">{fact.label}</p>
                  <p className="text-sm font-semibold text-slate-800 leading-tight">{fact.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-10">
            <div className="lg:col-span-3">
              <FadeIn>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-10 h-0.5 bg-orange-500" />
                  <span className="text-orange-500 text-sm font-semibold tracking-wider uppercase">Overview</span>
                </div>
                <h2 className="text-3xl font-bold text-slate-800 mb-4">
                  Why Study in <span className="text-orange-500">{country.name}?</span>
                </h2>
                <p className="text-slate-600 leading-relaxed mb-6">{country.overview}</p>
              </FadeIn>
            </div>
            <div className="lg:col-span-2">
              <FadeIn delay={0.15}>
                <div className="bg-slate-50 rounded-2xl p-6">
                  <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <Info size={18} className="text-orange-500" /> Quick Facts
                  </h3>
                  <div className="space-y-3">
                    {Object.entries(country.quickFacts).map(([key, val]) => (
                      <div key={key} className="flex justify-between items-start gap-2 text-sm">
                        <span className="text-slate-500 capitalize">{key.replace(/([A-Z])/g, " $1").trim()}</span>
                        <span className="text-slate-800 font-medium text-right max-w-[55%]">{val}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* Visa Requirements */}
      <section className="py-16 bg-slate-50 px-4">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="text-center mb-12">
              <div className="flex items-center justify-center gap-2 mb-3">
                <div className="w-10 h-0.5 bg-orange-500" />
                <span className="text-orange-500 text-sm font-semibold tracking-wider uppercase">Visa Info</span>
                <div className="w-10 h-0.5 bg-orange-500" />
              </div>
              <h2 className="text-3xl font-bold text-slate-800">
                {country.visa.type} <span className="text-orange-500">Requirements</span>
              </h2>
            </div>
          </FadeIn>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Visa Overview Card */}
            <FadeIn>
              <div className="bg-white rounded-2xl p-6 shadow-md h-full">
                <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center mb-4">
                  <FileCheck size={22} className="text-orange-500" />
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-3">Visa Overview</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Visa Type</span><span className="text-slate-800 font-medium">{country.visa.type}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Processing Time</span><span className="text-slate-800 font-medium">{country.visa.processingTime}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Application Fee</span><span className="text-slate-800 font-medium">{country.visa.fee}</span></div>
                </div>
              </div>
            </FadeIn>

            {/* Requirements */}
            <FadeIn delay={0.1}>
              <div className="bg-white rounded-2xl p-6 shadow-md h-full">
                <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center mb-4">
                  <AlertCircle size={22} className="text-orange-500" />
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-3">Documents Required</h3>
                <ul className="space-y-2">
                  {country.visa.requirements.map((req, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                      <CheckCircle size={14} className="text-orange-500 flex-shrink-0 mt-0.5" />
                      {req}
                    </li>
                  ))}
                </ul>
              </div>
            </FadeIn>

            {/* Tips */}
            <FadeIn delay={0.2}>
              <div className="bg-white rounded-2xl p-6 shadow-md h-full">
                <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center mb-4">
                  <Lightbulb size={22} className="text-orange-500" />
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-3">Expert Tips</h3>
                <ul className="space-y-3">
                  {country.visa.tips.map((tip, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                      <BadgeCheck size={14} className="text-green-500 flex-shrink-0 mt-0.5" />
                      {tip}
                    </li>
                  ))}
                </ul>
                <div className="mt-5 p-3 bg-orange-50 rounded-xl">
                  <p className="text-xs text-orange-700 font-medium">Need help with visa? Sun Lumia Overseas handles the entire process for you!</p>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Universities */}
      <section id="universities" className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="text-center mb-12">
              <div className="flex items-center justify-center gap-2 mb-3">
                <div className="w-10 h-0.5 bg-orange-500" />
                <span className="text-orange-500 text-sm font-semibold tracking-wider uppercase">Universities</span>
                <div className="w-10 h-0.5 bg-orange-500" />
              </div>
              <h2 className="text-3xl font-bold text-slate-800">
                Top 10 Universities in <span className="text-orange-500">{country.name}</span>
              </h2>
            </div>
          </FadeIn>

          <div className="space-y-4">
            {displayedUnis.map((uni, i) => (
              <FadeIn key={uni.rank} delay={i * 0.05}>
                <div className={`bg-white rounded-2xl shadow-md overflow-hidden border transition-all duration-300 ${
                  expandedUni === uni.rank ? "border-orange-200" : "border-transparent hover:border-orange-100"
                }`}>
                  <div
                    className="p-5 flex items-center gap-4 cursor-pointer"
                    onClick={() => setExpandedUni(expandedUni === uni.rank ? null : uni.rank)}
                  >
                    {/* Rank Badge */}
                    <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">#{uni.rank}</span>
                    </div>

                    {/* Main Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-800 text-base truncate">{uni.name}</h3>
                      <div className="flex flex-wrap items-center gap-3 mt-1">
                        <span className="text-xs text-slate-500 flex items-center gap-1"><MapPin size={12} /> {uni.location}</span>
                        <span className="text-xs text-orange-500 font-medium flex items-center gap-1"><Star size={12} /> {uni.worldRanking}</span>
                      </div>
                    </div>

                    {/* Quick Stats - Desktop */}
                    <div className="hidden md:flex items-center gap-6">
                      <div className="text-center">
                        <p className="text-xs text-slate-500">Tuition</p>
                        <p className="text-sm font-semibold text-slate-800">{uni.tuitionRange}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-slate-500">Acceptance</p>
                        <p className="text-sm font-semibold text-slate-800">{uni.acceptanceRate}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-slate-500">Intl Students</p>
                        <p className="text-sm font-semibold text-slate-800">{uni.intlStudents}</p>
                      </div>
                    </div>

                    <ChevronDown size={18} className={`text-slate-400 flex-shrink-0 transition-transform duration-300 ${expandedUni === uni.rank ? "rotate-180" : ""}`} />
                  </div>

                  {/* Expanded Content */}
                  <motion.div
                    initial={false}
                    animate={{ height: expandedUni === uni.rank ? "auto" : 0, opacity: expandedUni === uni.rank ? 1 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-5 pb-5 pt-0 border-t border-slate-100">
                      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                        {/* Mobile Stats */}
                        <div className="sm:hidden grid grid-cols-3 gap-3 col-span-full">
                          <div className="bg-slate-50 rounded-lg p-3 text-center">
                            <p className="text-xs text-slate-500">Tuition</p>
                            <p className="text-sm font-semibold text-slate-800">{uni.tuitionRange}</p>
                          </div>
                          <div className="bg-slate-50 rounded-lg p-3 text-center">
                            <p className="text-xs text-slate-500">Acceptance</p>
                            <p className="text-sm font-semibold text-slate-800">{uni.acceptanceRate}</p>
                          </div>
                          <div className="bg-slate-50 rounded-lg p-3 text-center">
                            <p className="text-xs text-slate-500">Intl Students</p>
                            <p className="text-sm font-semibold text-slate-800">{uni.intlStudents}</p>
                          </div>
                        </div>

                        <div className="col-span-full">
                          <p className="text-sm font-medium text-slate-700 mb-2">Popular Courses</p>
                          <div className="flex flex-wrap gap-2">
                            {uni.popularCourses.map((course, j) => (
                              <span key={j} className="px-3 py-1.5 bg-orange-50 text-orange-700 rounded-lg text-xs font-medium">
                                {course}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </FadeIn>
            ))}
          </div>

          {!showAllUnis && (
            <div className="text-center mt-8">
              <button
                onClick={() => setShowAllUnis(true)}
                className="px-6 py-3 bg-orange-50 text-orange-600 rounded-lg font-medium hover:bg-orange-100 transition-colors flex items-center gap-2 mx-auto"
              >
                Show All 10 Universities <ChevronDown size={16} />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Popular Courses */}
      <section className="py-16 bg-slate-900 px-4">
        <div className="max-w-7xl mx-auto">
          <FadeIn>
            <div className="text-center mb-12">
              <div className="flex items-center justify-center gap-2 mb-3">
                <div className="w-10 h-0.5 bg-orange-500" />
                <span className="text-orange-400 text-sm font-semibold tracking-wider uppercase">Programs</span>
                <div className="w-10 h-0.5 bg-orange-500" />
              </div>
              <h2 className="text-3xl font-bold text-white">
                Popular Courses in <span className="text-orange-400">{country.name}</span>
              </h2>
            </div>
          </FadeIn>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {country.popularCourses.map((course, i) => {
              const Icon = iconMap[course.icon] || BookOpen;
              return (
                <FadeIn key={i} delay={i * 0.06}>
                  <div className="bg-slate-800 rounded-2xl p-5 hover:bg-slate-800/80 transition-all group hover:-translate-y-1 duration-300">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 bg-orange-500/10 rounded-xl flex items-center justify-center group-hover:bg-orange-500/20 transition-colors">
                        <Icon size={20} className="text-orange-400" />
                      </div>
                      <DemandBadge demand={course.demand} />
                    </div>
                    <h3 className="text-white font-medium text-sm">{course.name}</h3>
                    <p className="text-slate-400 text-xs mt-1">Demand: {course.demand}</p>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-gradient-to-r from-orange-500 to-orange-600">
        <div className="max-w-4xl mx-auto text-center">
          <FadeIn>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Study in {country.name}?
            </h2>
            <p className="text-orange-100 text-lg mb-8 max-w-2xl mx-auto">
              Get expert guidance from Sun Lumia Overseas. Free counselling, visa support, and university admission assistance.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="px-8 py-3.5 bg-white text-orange-600 rounded-lg font-semibold hover:bg-orange-50 transition-all hover:shadow-lg flex items-center gap-2">
                Apply Now <ArrowRight size={16} />
              </Link>
              <a href={`tel:${companyInfo.phone.replace(/\s/g, "")}`} className="px-8 py-3.5 border-2 border-white text-white rounded-lg font-semibold hover:bg-white/10 transition-all">
                Call {companyInfo.phone}
              </a>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default CountryDetailPage;
